if (!customElements.get('tan-spotlight-directory')) {
  class TanSpotlightDirectory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.grid = this.querySelector('[data-spotlight-grid]');
      this.countrySelect = this.querySelector('[data-country-filter]');
      this.typeSelect = this.querySelector('[data-type-filter]');
      this.searchInput = this.querySelector('[data-search-filter]');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.countNode = this.querySelector('[data-results-count]');
      this.emptyNode = this.querySelector('[data-empty-results]');

      if (!this.grid) return;

      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 12);
      this.feedBatchSize = Math.max(this.pageSize, Number(this.dataset.feedBatchSize) || 48);
      this.projectCount = Math.max(0, Number(this.dataset.projectCount) || 0);
      this.visibleLimit = this.pageSize;
      this.activeIndustry = 'all';
      this.indexRecords = null;
      this.indexByHandle = new Map();
      this.indexOrder = new Map();
      this.cardMap = new Map();
      this.loadedBatches = new Set();
      this.batchPromises = new Map();
      this.feedPageUrls = new Map([
        [1, this.sectionUrl(this.dataset.feedSectionId || 'tan-spotlight-feed')]
      ]);
      this.requestToken = 0;
      this.indexFailed = false;
      this.searchTimer = null;

      this.refreshCards();
      if (!this.cardMap.size) return;

      this.bindEvents();
      this.dataset.ready = 'true';
      this.initialize();
    }

    async initialize() {
      try {
        await this.loadIndex();
        this.populateControls();
        this.restoreFromUrl();
        await this.applyFilters({ updateUrl: false });
      } catch (error) {
        this.indexFailed = true;
        this.restoreFromUrl();
        this.applyLoadedFilters({ updateUrl: false });
        console.warn('Business Spotlight index could not be loaded.', error);
      }
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const industryButton = event.target.closest('[data-industry-filter]');
        if (industryButton && this.contains(industryButton)) {
          this.activeIndustry = industryButton.dataset.industryFilter || 'all';
          this.visibleLimit = this.pageSize;
          this.updateIndustryButtons();
          this.applyFilters();
          return;
        }

        const resetButton = event.target.closest('[data-reset-filters]');
        if (resetButton && this.contains(resetButton)) {
          this.resetFilters();
          return;
        }

        const loadMoreButton = event.target.closest('[data-load-more]');
        if (loadMoreButton && this.contains(loadMoreButton)) {
          this.loadMore();
        }
      });

      [this.countrySelect, this.typeSelect].forEach((select) => {
        select?.addEventListener('change', () => {
          this.visibleLimit = this.pageSize;
          this.applyFilters();
        });
      });

      this.searchInput?.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => {
          this.visibleLimit = this.pageSize;
          this.applyFilters();
        }, 180);
      });
    }

    async loadIndex() {
      const response = await fetch(this.sectionUrl(this.dataset.indexSectionId || 'tan-spotlight-index'), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Index request failed with ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const indexNode = documentFragment.querySelector('[data-spotlight-index]');
      if (!indexNode) throw new Error('Index payload was not found');

      const payload = JSON.parse(indexNode.textContent);
      const records = Array.isArray(payload.projects) ? payload.projects : [];
      this.feedBatchSize = Math.max(this.pageSize, Number(payload.batchSize) || this.feedBatchSize);
      this.indexRecords = records
        .filter((record) => record && record.handle)
        .map((record) => ({
          ...record,
          displayOrder: Number(record.displayOrder) || Number(record.sourceIndex) || Number.MAX_SAFE_INTEGER,
          sourceIndex: Number(record.sourceIndex) || Number.MAX_SAFE_INTEGER,
          batch: Math.max(1, Number(record.batch) || 1),
          search: String(record.search || '').toLocaleLowerCase()
        }))
        .sort((a, b) => a.displayOrder - b.displayOrder || a.sourceIndex - b.sourceIndex);

      this.projectCount = this.indexRecords.length;
      this.indexByHandle = new Map(this.indexRecords.map((record) => [record.handle, record]));
      this.indexOrder = new Map(this.indexRecords.map((record, index) => [record.handle, index]));
    }

    populateControls() {
      this.populateSelect(this.countrySelect, this.indexRecords, 'country', 'countryLabel');
      this.populateSelect(this.typeSelect, this.indexRecords, 'projectType', 'projectTypeLabel');

      const industryRow = this.querySelector('.tan-spotlight-directory__industry-row');
      const allButton = industryRow?.querySelector('[data-industry-filter="all"]');
      if (industryRow && allButton) {
        industryRow.querySelectorAll('[data-industry-filter]:not([data-industry-filter="all"])').forEach((button) => button.remove());
        const seen = new Set();
        this.indexRecords.forEach((record) => {
          const value = String(record.industry || '');
          const label = String(record.industryLabel || '');
          if (!value || !label || seen.has(value)) return;
          seen.add(value);
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.industryFilter = value;
          button.setAttribute('aria-pressed', 'false');
          button.textContent = label;
          industryRow.append(button);
        });
      }
    }

    populateSelect(select, records, valueKey, labelKey) {
      if (!select) return;
      const firstOption = select.options[0];
      select.replaceChildren(firstOption);
      const seen = new Set();
      records.forEach((record) => {
        const value = String(record[valueKey] || '');
        const label = String(record[labelKey] || '');
        if (!value || !label || seen.has(value)) return;
        seen.add(value);
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        select.append(option);
      });
    }

    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const industry = params.get('industry') || 'all';
      const country = params.get('country') || 'all';
      const type = params.get('type') || 'all';
      const query = params.get('q') || '';

      if (industry === 'all' || this.querySelector(`[data-industry-filter="${CSS.escape(industry)}"]`)) {
        this.activeIndustry = industry;
      }
      if (this.countrySelect?.querySelector(`option[value="${CSS.escape(country)}"]`)) this.countrySelect.value = country;
      if (this.typeSelect?.querySelector(`option[value="${CSS.escape(type)}"]`)) this.typeSelect.value = type;
      if (this.searchInput) this.searchInput.value = query;
      this.updateIndustryButtons();
    }

    updateIndustryButtons() {
      this.querySelectorAll('[data-industry-filter]').forEach((button) => {
        const isActive = button.dataset.industryFilter === this.activeIndustry;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    resetFilters() {
      this.activeIndustry = 'all';
      this.visibleLimit = this.pageSize;
      if (this.countrySelect) this.countrySelect.value = 'all';
      if (this.typeSelect) this.typeSelect.value = 'all';
      if (this.searchInput) this.searchInput.value = '';
      this.updateIndustryButtons();
      this.applyFilters();
      this.searchInput?.focus();
    }

    async loadMore() {
      if (this.indexFailed || !this.indexRecords) {
        const fallbackUrl = this.dataset.nextPageUrl;
        if (fallbackUrl) window.location.assign(fallbackUrl);
        return;
      }

      this.visibleLimit += this.pageSize;
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = true;
        this.loadMoreButton.setAttribute('aria-busy', 'true');
      }
      try {
        await this.applyFilters({ updateUrl: false });
      } finally {
        if (this.loadMoreButton) {
          this.loadMoreButton.disabled = false;
          this.loadMoreButton.removeAttribute('aria-busy');
        }
      }
    }

    async applyFilters({ updateUrl = true } = {}) {
      if (!this.indexRecords) {
        this.applyLoadedFilters({ updateUrl });
        return;
      }

      const requestToken = ++this.requestToken;
      const state = this.filterState();
      const matchingRecords = this.indexRecords.filter((record) => this.recordMatches(record, state));
      const targetRecords = matchingRecords.slice(0, this.visibleLimit);

      try {
        await this.ensureRecordsLoaded(targetRecords);
      } catch (error) {
        console.warn('Business Spotlight projects could not be loaded.', error);
      }
      if (requestToken !== this.requestToken) return;

      this.refreshCards();
      this.reorderLoadedCards();
      this.hideAllCards();

      let shown = 0;
      targetRecords.forEach((record) => {
        const card = this.cardMap.get(record.handle);
        if (!card) return;
        this.hydrateCard(card);
        card.hidden = false;
        shown += 1;
      });

      this.updateResults(shown, matchingRecords.length);
      this.classList.toggle('has-active-filters', this.hasActiveFilters());
      if (updateUrl) this.updateUrl(state);
    }

    applyLoadedFilters({ updateUrl = true } = {}) {
      const state = this.filterState();
      const matchingCards = Array.from(this.cardMap.values()).filter((card) => this.cardMatches(card, state));
      this.hideAllCards();
      matchingCards.slice(0, this.visibleLimit).forEach((card) => {
        this.hydrateCard(card);
        card.hidden = false;
      });

      const total = this.hasActiveFilters() ? matchingCards.length : Math.max(this.projectCount, matchingCards.length);
      const shown = Math.min(matchingCards.length, this.visibleLimit);
      this.updateResults(shown, total);
      this.classList.toggle('has-active-filters', this.hasActiveFilters());
      if (updateUrl) this.updateUrl(state);
    }

    filterState() {
      return {
        industry: this.activeIndustry,
        country: this.countrySelect?.value || 'all',
        type: this.typeSelect?.value || 'all',
        query: (this.searchInput?.value || '').trim().toLocaleLowerCase()
      };
    }

    recordMatches(record, state) {
      return (state.industry === 'all' || record.industry === state.industry)
        && (state.country === 'all' || record.country === state.country)
        && (state.type === 'all' || record.projectType === state.type)
        && (!state.query || record.search.includes(state.query));
    }

    cardMatches(card, state) {
      return (state.industry === 'all' || card.dataset.industry === state.industry)
        && (state.country === 'all' || card.dataset.country === state.country)
        && (state.type === 'all' || card.dataset.projectType === state.type)
        && (!state.query || (card.dataset.search || '').includes(state.query));
    }

    async ensureRecordsLoaded(records) {
      const batches = new Set();
      records.forEach((record) => {
        if (!this.cardMap.has(record.handle)) batches.add(record.batch);
      });
      await Promise.all(Array.from(batches).map((batch) => this.fetchBatch(batch)));
    }

    fetchBatch(batch) {
      if (this.loadedBatches.has(batch)) return Promise.resolve();
      if (this.batchPromises.has(batch)) return this.batchPromises.get(batch);

      const promise = (async () => {
        if (batch > 1 && !this.feedPageUrls.has(batch)) await this.fetchBatch(batch - 1);
        await this.fetchBatchContent(batch);
        this.loadedBatches.add(batch);
      })()
        .finally(() => this.batchPromises.delete(batch));
      this.batchPromises.set(batch, promise);
      return promise;
    }

    async fetchBatchContent(batch) {
      const pageUrl = this.feedPageUrls.get(batch);
      if (!pageUrl) throw new Error(`Project page ${batch} was not found`);

      const response = await fetch(pageUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Project request failed with ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const feed = documentFragment.querySelector('[data-spotlight-feed]');
      if (!feed) throw new Error('Project payload was not found');

      if (feed.dataset.feedNextUrl) {
        this.feedPageUrls.set(batch + 1, this.feedSectionUrl(feed.dataset.feedNextUrl));
      }

      const fragment = document.createDocumentFragment();
      feed.querySelectorAll('[data-spotlight-card]').forEach((card) => {
        const handle = card.dataset.projectHandle;
        if (!handle || this.cardMap.has(handle)) return;
        this.applyCurrentLabels(card);
        card.hidden = true;
        fragment.append(card);
        this.cardMap.set(handle, card);
      });
      this.grid.append(fragment);
    }

    applyCurrentLabels(card) {
      const readStoryLabel = this.dataset.readStoryLabel || '';
      const featuredLabel = this.dataset.featuredLabel || '';
      const businessName = card.querySelector('.tan-spotlight-card__identity p')?.textContent.trim()
        || this.dataset.businessNameFallback
        || '';
      const contentLink = card.querySelector('a.tan-spotlight-card__content');
      const readStoryAria = String(this.dataset.readStoryAria || '')
        .replace(/\{label\}/g, readStoryLabel)
        .replace(/\{business\}/g, businessName);
      if (contentLink && readStoryAria) contentLink.setAttribute('aria-label', readStoryAria);

      card.querySelectorAll('[data-read-story-label]').forEach((node) => {
        const icon = node.querySelector('i');
        node.textContent = `${readStoryLabel} `;
        if (icon) node.append(icon);
      });
      card.querySelectorAll('[data-featured-label]').forEach((node) => {
        node.textContent = featuredLabel;
      });
    }

    sectionUrl(sectionId) {
      const current = new URL(window.location.href);
      const url = new URL(window.location.pathname, window.location.origin);
      ['preview_theme_id', 'pb', '_fd'].forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    }

    feedSectionUrl(paginateUrl) {
      const current = new URL(window.location.href);
      const url = new URL(paginateUrl, window.location.origin);
      ['preview_theme_id', 'pb', '_fd'].forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      url.searchParams.set('section_id', this.dataset.feedSectionId || 'tan-spotlight-feed');
      return url.toString();
    }

    refreshCards() {
      this.cards = Array.from(this.grid.querySelectorAll('[data-spotlight-card]'));
      this.cards.forEach((card) => {
        const handle = card.dataset.projectHandle;
        if (handle) this.cardMap.set(handle, card);
      });
    }

    reorderLoadedCards() {
      if (!this.indexOrder.size) return;
      const cards = Array.from(this.cardMap.values()).sort((a, b) => {
        const aOrder = this.indexOrder.get(a.dataset.projectHandle) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = this.indexOrder.get(b.dataset.projectHandle) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
      const fragment = document.createDocumentFragment();
      cards.forEach((card) => fragment.append(card));
      this.grid.append(fragment);
    }

    hideAllCards() {
      this.cardMap.forEach((card) => {
        card.hidden = true;
        card.querySelectorAll('tan-spotlight-media').forEach((carousel) => carousel.pauseAll?.());
      });
    }

    hydrateCard(card) {
      if (!card?.hasAttribute('data-deferred-card')) return;

      card.querySelectorAll('img[data-deferred-image]').forEach((image) => {
        const src = image.dataset.src;
        const srcset = image.dataset.srcset;
        if (srcset) image.setAttribute('srcset', srcset);
        if (src) image.setAttribute('src', src);
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-deferred-image');
      });

      card.querySelectorAll('[data-deferred-video-shell]').forEach((shell) => {
        const poster = shell.dataset.poster;
        const video = shell.querySelector('video');
        if (poster && video) {
          video.setAttribute('poster', poster);
          video.querySelector('img')?.setAttribute('src', poster);
        }
        shell.removeAttribute('data-poster');
        shell.removeAttribute('data-deferred-video-shell');
      });

      card.removeAttribute('data-deferred-card');
    }

    updateResults(shown, total) {
      if (this.countNode) {
        this.countNode.textContent = String(this.dataset.resultsTemplate || '')
          .replace(/\{shown\}/g, String(shown))
          .replace(/\{total\}/g, String(total));
      }
      if (this.emptyNode) this.emptyNode.hidden = total !== 0;
      if (this.loadMoreButton) this.loadMoreButton.hidden = total === 0 || shown >= total;
    }

    hasActiveFilters() {
      return this.activeIndustry !== 'all'
        || (this.countrySelect?.value || 'all') !== 'all'
        || (this.typeSelect?.value || 'all') !== 'all'
        || Boolean((this.searchInput?.value || '').trim());
    }

    updateUrl(state) {
      const params = new URLSearchParams(window.location.search);
      ['industry', 'country', 'type', 'q', 'page'].forEach((key) => params.delete(key));

      if (state.industry !== 'all') params.set('industry', state.industry);
      if (state.country !== 'all') params.set('country', state.country);
      if (state.type !== 'all') params.set('type', state.type);
      if (state.query) params.set('q', state.query);

      const nextUrl = `${window.location.pathname}${params.size ? `?${params.toString()}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }

  customElements.define('tan-spotlight-directory', TanSpotlightDirectory);
}
